import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../api';
import { Logo } from '../components/Logo';

interface PublicRace {
  id: string;
  name: string;
  raceDate: string;
  live: boolean;
  final: boolean;
  finishers: number;
}

const prettyDate = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

/** Races whose organisers published results: proof it works, and the way in for runners. */
function RecentResults() {
  const races = useQuery({ queryKey: ['public-races'], queryFn: () => api<{ races: PublicRace[] }>('/public/races', { auth: 'none' }), refetchInterval: 60_000 });
  const list = races.data?.races ?? [];
  return (
    <section className="stack" aria-labelledby="recent-results" style={{ gap: 12 }}>
      <h2 id="recent-results" className="section-title" style={{ margin: 0 }}>
        Recent results
      </h2>
      {races.isLoading && <p className="muted" style={{ margin: 0 }}>Loading…</p>}
      {races.data && list.length === 0 && (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            Results from races timed with Splitline appear here when their organisers publish them.
          </p>
        </div>
      )}
      {list.length > 0 && (
        <div className="grid-cards">
          {list.map((r) => (
            <Link key={r.id} to={`/results/${r.id}`} className="card stack result-card" style={{ gap: 6 }}>
              <div className="inline" style={{ justifyContent: 'space-between' }}>
                <span className="small muted">{prettyDate(r.raceDate)}</span>
                {r.live ? <span className="chip ok">Live</span> : r.final ? <span className="chip info">Final</span> : <span className="chip neutral">Provisional</span>}
              </div>
              <div className="display" style={{ fontSize: 24, lineHeight: 1.1 }}>
                {r.name}
              </div>
              <div className="small">
                {r.finishers} finisher{r.finishers === 1 ? '' : 's'} · <span style={{ color: 'var(--link)', fontWeight: 600 }}>See results →</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

/** What visitors who aren't signed in see at the root. */
export function LandingPage() {
  return (
    <>
      <header className="bar">
        <div className="bar-inner">
          <Link to="/" className="brand">
            <Logo />
            <span>Splitline</span>
          </Link>
          <nav aria-label="Primary" className="nav">
            <Link to="/docs">Guide</Link>
            <Link to="/station/check">Check a phone</Link>
          </nav>
          <div className="bar-right">
            <Link to="/login" style={{ color: 'var(--bar-muted)' }}>
              Sign in
            </Link>
            <Link to="/signup" className="btn primary small">
              Sign up free
            </Link>
          </div>
        </div>
      </header>
      <main className="page landing" style={{ gap: 28 }}>
        <section className="landing-hero">
          <div className="stack" style={{ gap: 14 }}>
            <h1 className="display" style={{ fontSize: 'clamp(36px, 6vw, 60px)', lineHeight: 1, margin: 0 }}>
              Race timing with the phones you already have
            </h1>
            <p style={{ fontSize: 19, margin: 0, color: 'var(--ink-2)' }}>
              No chips, no mats, no rental. Volunteers prop phones at the start, finish and splits; Splitline watches the video, reads bibs and times every runner. You check anything it isn’t sure about, then publish results.
            </p>
            <div className="inline">
              <Link to="/signup" className="btn primary">
                Create a free account
              </Link>
              <Link to="/docs" className="btn">
                Read the setup guide
              </Link>
            </div>
            <p className="small muted" style={{ margin: 0 }}>
              Free during the beta for races up to 1,000 runners. Bring your roster as a CSV or Excel export from any registration site.
            </p>
          </div>
        </section>

        <section className="grid-cards" aria-label="How it works">
          {[
            ['1. Set up', 'Create the race, import your roster, and add a station for each phone. Volunteers check their phones ahead of time in 30 seconds.'],
            ['2. Race day', 'Volunteers pair with a code, line the camera up with the finish and press record. Video uploads as they go; times appear within a minute or two.'],
            ['3. Results', 'Clear bib reads are matched automatically. Hidden bibs are matched by what runners wore at other points. You confirm the rest, then share a link or export CSV.'],
          ].map(([title, text]) => (
            <div key={title} className="card stack" style={{ gap: 8 }}>
              <h2 style={{ margin: 0 }}>{title}</h2>
              <p style={{ margin: 0 }}>{text}</p>
            </div>
          ))}
        </section>

        <RecentResults />

        <section className="grid-cards" aria-label="What to expect">
          <div className="card stack" style={{ gap: 8 }}>
            <h2 style={{ margin: 0 }}>Honest about accuracy</h2>
            <p style={{ margin: 0 }}>
              A well-placed timing phone is within a few hundredths of a second. Splitline never guesses: a crossing it isn’t sure of goes to you, not into someone’s result. Plan on some review after the race, and keep a hand-timed backup for your first events.
            </p>
            <Link to="/docs/accuracy">What to expect →</Link>
          </div>
          <div className="card stack" style={{ gap: 8 }}>
            <h2 style={{ margin: 0 }}>Good fits</h2>
            <p style={{ margin: 0 }}>Club races, parkrun-style events, school cross-country, fun runs and charity 5Ks: up to a few hundred runners, where chip timing is too expensive and a stopwatch is too slow.</p>
            <Link to="/docs/getting-started">Getting started →</Link>
          </div>
        </section>
      </main>
      <footer className="page small muted" style={{ paddingTop: 0 }}>
        <div className="inline" style={{ gap: 16 }}>
          <Link to="/docs">Guide</Link>
          <Link to="/docs/terms">Terms</Link>
          <Link to="/docs/privacy-policy">Privacy</Link>
          <Link to="/help">Volunteer card</Link>
        </div>
      </footer>
    </>
  );
}
